import React, { FC, ReactNode } from 'react';
import { Flex, Tag, Typography } from '@duxton-web/ui';

import { Rollout, RolloutTarget } from '@/lib/PlannerDTOs';
import FullDivider from '@/pages/ExperimentFormV2/common/FullDivider';
import RolloutDiffConstraintV2 from '@/pages/RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffConstraintV2';
import RolloutDiffConstraintValue from '@/pages/RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffConstraintValue';
import RolloutDiffIndex from '@/pages/RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffIndex';
import {
  DIFF_SHOWFOR,
  DiffShowFor_Keys,
} from '@/pages/RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffUtils';
import RolloutDiffValueV2 from '@/pages/RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffValueV2';

export const ConstraintsViewerType = {
  ROLLOUT: 'rollout',
  EXPERIMENT: 'experiment',
} as const;

export type ConstraintsViewerType_Keys = keyof typeof ConstraintsViewerType;

interface IConstraintsViewerProps {
  rollout: Rollout;
  rolloutTarget: RolloutTarget;
  rolloutToCompare?: Rollout;
  rolloutTargetToCompare?: RolloutTarget;
  position: number;
  showFor?: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys];
  variableType: string;
  domain: string;
  type: (typeof ConstraintsViewerType)[ConstraintsViewerType_Keys];
  targetValueRenderer?: ReactNode;
  showValue?: boolean;
}

const ConstraintsViewer: FC<IConstraintsViewerProps> = ({
  rollout,
  rolloutTarget,
  rolloutToCompare,
  rolloutTargetToCompare,
  position,
  showFor,
  variableType,
  domain,
  type,
  targetValueRenderer = <div />,
  showValue = true,
}) => {
  const renderValue = (
    varType: string,
    compareTarget: RolloutTarget,
    baseTarget: RolloutTarget,
    showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys] = DIFF_SHOWFOR.ALL,
  ) => {
    if (!showValue) return null;

    return (
      <Flex gap={8} vertical>
        <Typography.Text strong type="secondary">
          Value
        </Typography.Text>
        <RolloutDiffValueV2
          variableType={varType}
          baseTarget={baseTarget}
          compareTarget={compareTarget}
          showFor={showFor}
        />
      </Flex>
    );
  };

  const renderConstraint = (
    baseTarget: RolloutTarget,
    compareTarget: RolloutTarget,
    showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys] = DIFF_SHOWFOR.ALL,
    compact: boolean = false,
  ) => {
    if (!baseTarget.constraints) {
      return null;
    }

    return (
      <RolloutDiffConstraintV2
        baseConstraints={baseTarget?.constraints || []}
        compareConstraints={compareTarget?.constraints || []}
        showFor={showFor}
        compact={compact}
      />
    );
  };

  const renderRolloutTargetValue = (
    rollout: Rollout,
    rolloutTarget: RolloutTarget,
    rolloutToCompare: Rollout,
    rolloutTargetToCompare: RolloutTarget,
    position: number,
    showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys] = DIFF_SHOWFOR.ALL,
  ) => {
    if (position === -1)
      return renderValue(
        variableType,
        rolloutTargetToCompare!,
        rolloutTarget,
        showFor,
      );

    return (
      <div className="rollout-target-value-wrapper">
        <div className="rollout-target-value-value-wrapper">
          <RolloutDiffConstraintValue
            domain={domain}
            baseRollout={rollout}
            baseTarget={rolloutTarget}
            compareRollout={rolloutToCompare}
            compareTarget={rolloutTargetToCompare}
            showFor={showFor}
            compact={true}
          />

          <div>
            {renderValue(
              variableType,
              rolloutTargetToCompare!,
              rolloutTarget,
              showFor,
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTargetValue = (
    rollout: Rollout,
    rolloutTarget: RolloutTarget,
    rolloutToCompare: Rollout,
    rolloutTargetToCompare: RolloutTarget,
    position: number,
    showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys] = DIFF_SHOWFOR.ALL,
  ) => {
    if (type === ConstraintsViewerType.ROLLOUT)
      return renderRolloutTargetValue(
        rollout,
        rolloutTarget,
        rolloutToCompare,
        rolloutTargetToCompare,
        position,
        showFor,
      );

    return targetValueRenderer;
  };

  const renderRolloutTargetConstraints = (
    rolloutTarget: RolloutTarget,
    rolloutTargetToCompare: RolloutTarget,
    showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys] = DIFF_SHOWFOR.ALL,
  ): ReactNode => {
    if (!rolloutTarget?.constraints?.length) return false;

    return (
      <Flex gap={8} vertical>
        <Typography.Text strong type="secondary">
          Target
        </Typography.Text>
        <FullDivider
          orientation="left"
          orientationMargin={0}
          style={{ margin: 0 }}
        >
          <Tag color="#3D3D3D" style={{ margin: 0 }} size="xsmall">
            Constraints
          </Tag>
        </FullDivider>
        {renderConstraint(rolloutTarget, rolloutTargetToCompare, showFor, true)}
      </Flex>
    );
  };

  return (
    <Flex vertical gap={8} style={{ height: '100%', overflow: 'scroll' }}>
      {position > 0 && (
        <RolloutDiffIndex
          baseRollout={rollout}
          baseTarget={rolloutTarget}
          compareRollout={rolloutToCompare}
          compareTarget={rolloutTargetToCompare}
          showFor={showFor}
          index={position}
          alternateLabel={
            type === ConstraintsViewerType.EXPERIMENT ? 'Experiment' : ''
          }
        />
      )}

      {renderRolloutTargetConstraints(
        rolloutTarget,
        rolloutTargetToCompare,
        showFor,
      )}

      {renderTargetValue(
        rollout,
        rolloutTarget,
        rolloutToCompare,
        rolloutTargetToCompare,
        position,
        showFor,
      )}
    </Flex>
  );
};

export default ConstraintsViewer;
